import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, Button, DatePicker, Icon, Form, message, Tooltip, Tag } from "antd";
import moment from "moment";
import { alertMessage, randomUUID } from "../../common/Common";
import {
  uploadCustomsClearanceFile,
  deleteOrderPic,
  updateOrderDetails_v2,
  downloadOrderPictures,
  sendPodImagesToEmails,
} from "../../api/OrdersApi";
import { printPDF, renderAlertMessage } from "../../helpers/common";
import { renderNotification } from "../../components/orders/OrderTypeIcons";
import {
  CheckMarkIcon,
  CircleCheckIcon,
  CustomsOfficerVerifedIcon,
  DotedCircleIcon,
  dotedCircleSvg,
  EdiIcon,
  EditPennIcon,
  SelectedTickMarkIcon,
  TrashIcon,
  DownloadIcon,
  PrintIcon,
  SendAsEmailIcon,
  CustomsOfficerIcon,
} from "../../common/CustomIcon";
import UploadButton_v2 from "./UploadButton_v2";
import "./CustomsInformation.scss";
import Button_v2 from "./Button_v2";
import FilePreviewer from "./FilePreviewer";
import PODEmailDrawer from "./PODEmailDrawer";
import BulkAttachmentUpload from "./BulkAttachmentUpload";

export const CUSTOMS_DOC_TYPES = [
  { code: "DELIVERY_ORDER", label: "delivery order" },
  { code: "CUSTOMS_RELEASE_DOCUMENT", label: "customs release document" },
  { code: "COPY_OF_CHECK_ACH_NUMBER", label: "copy of check" },
  { code: "CUSTOMS_STATUS_LOG", label: "customs status log (from descartes)" },
  { code: "MAWB_HAWB", label: "MAWB/HAWB" },
  { code: "CARGO_MANIFEST", label: "cargo manifest" },
  { code: "OTHER ATTACHMENT", label: "OTHER ATTACHMENT" },
];

// Legacy customs catch-all codes still map into OTHER ATTACHMENT
const CUSTOMS_OTHER_ALIASES = ["OTHERS", "OTHER ATTACHMENT", "OTHER ATTACHMENTS"];

const CustomsInformation = ({
  getFieldDecorator,
  orderDetails,
  currentOrder,
  onCustomsDataChange,
  viewMode = false,
  currentOrg = null,
  refreshDetails = () => {},
}) => {
  const [documents, setDocuments] = useState([]);
  const [stagedDocs, setStagedDocs] = useState([]);
  const [stagedDocsUploadedMap, setStagedDocsUploadedMap] = useState({});
  const [isUploadingAll, setIsUploadingAll] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState(null);
  const [previewFileType, setPreviewFileType] = useState(null);
  const [show, setShow] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [previewdoc, setPreviewdoc] = useState([]);
  const [isPODemailModalVisible, setIsPODemailModalVisible] = useState(false);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [PODemailData, setPODemailData] = useState({
    emails: [],
    description: "Please find the attached customs documents.",
  });
  const skipStagedClearOnCloseRef = useRef(false);

  const details = orderDetails || {};
  const orderId = details.id || currentOrder?.id || "";
  const locations = currentOrder?.locations || details.locations || [];
  const locationId = locations[0]?.location_id || locations[0]?.id || "";
  const mapFileInputId = `customs-map-file-input_${orderId || locationId || "new"}`;
  const locObj = { ...(locations[0] || {}), id: locationId };
  const predefinedDocsCode = CUSTOMS_DOC_TYPES.map((t) => t.code);
  const customsPicSettings = CUSTOMS_DOC_TYPES.map((t) => ({
    pic_code: t.code,
    pic_title: t.label,
  }));
  const actionType = viewMode || orderId ? "view" : "create";
  // FilePreviewer expects account.account_id + customer_order_details.organization_id
  const previewOrder = {
    ...(currentOrder || {}),
    id: orderId || currentOrder?.id,
    account: {
      ...(currentOrder?.account || {}),
      account_id:
        currentOrder?.account?.account_id ||
        details.account_id ||
        currentOrder?.customer_order_details?.account_id ||
        currentOrder?.account_id,
    },
    customer_order_details: currentOrder?.customer_order_details || details,
  };

  // NEW: track the actual date values so they can be sent up via onCustomsDataChange
  const [freightArrivalDateVal, setFreightArrivalDateVal] = useState(details.freight_arrival_date || null);
  const [generalOrderDateVal, setGeneralOrderDateVal] = useState(details.general_order_date || null);
  const [lastFreeDayVal, setLastFreeDayVal] = useState(details.last_free_day || null);
  const [customsClearedDateVal, setCustomsClearedDateVal] = useState(details.customs_cleared_date || null);
  const [isCustomsCleared, setIsCustomsCleared] = useState(!!details.is_customs_cleared);

  // Check if every document type in CUSTOMS_DOC_TYPES has at least one uploaded or staged document
  const allDocsUploaded = CUSTOMS_DOC_TYPES.every((type) =>
    documents.some((d) => d.pic_code === type.code) ||
    stagedDocs.some((d) => d.pic_code === type.code)
  );

  useEffect(() => {
    onCustomsDataChange?.({
      documents,
      stagedDocs,
      stagedDocsUploadedMap,
      isUploadingAll,
      previewVisible,
      previewFileUrl,
      previewFileType,
      show,
      selectedDocs,
    // NEW
    freight_arrival_date: freightArrivalDateVal,
    general_order_date: generalOrderDateVal,
    last_free_day: lastFreeDayVal,
    customs_cleared_date: customsClearedDateVal,
  });
}, [
  documents,
  stagedDocs,
  stagedDocsUploadedMap,
  isUploadingAll,
  previewVisible,
  previewFileUrl,
  previewFileType,
  show,
  selectedDocs,
  // NEW
  freightArrivalDateVal,
  generalOrderDateVal,
  lastFreeDayVal,
  customsClearedDateVal,
]);

useEffect(() => {
  setFreightArrivalDateVal(orderDetails?.freight_arrival_date || null);
  setGeneralOrderDateVal(orderDetails?.general_order_date || null);
  setLastFreeDayVal(orderDetails?.last_free_day || null);
  setCustomsClearedDateVal(orderDetails?.customs_cleared_date || null);
  setIsCustomsCleared(!!orderDetails?.is_customs_cleared || null);
}, [orderDetails?.freight_arrival_date, orderDetails?.general_order_date, orderDetails?.last_free_day, orderDetails?.customs_cleared_date]);

  useEffect(() => {
    const docs = [];
    const locations = currentOrder?.locations || orderDetails?.locations || [];
    if (locations.length > 0) {
      for (const loc of locations) {
        if (loc?.documents) {
          for (const doc of loc.documents) {
            const docCode = (doc?.pic_code || "").toUpperCase();
            const docTitle = (doc?.pic_title || "").toUpperCase();
            const imageType = (doc?.image_type || doc?.picture_type || "").toLowerCase();
            for (const type of CUSTOMS_DOC_TYPES) {
              const isOtherBucket = type.code === "OTHER ATTACHMENT";
              const matchesType =
                docCode === type.code ||
                docTitle === type.label.toUpperCase() ||
                (type.code === "DELIVERY_ORDER" && docCode === "IMPORT") ||
                (isOtherBucket && CUSTOMS_OTHER_ALIASES.includes(docCode));
              if (!matchesType) continue;

              // Keep Documents vs Customs OTHER ATTACHMENT separate:
              // Documents uses image_type "normal"; Customs uses "import".
              if (isOtherBucket) {
                const isCustomsOther =
                  imageType === "import" ||
                  (!imageType && !!doc.picture_obj);
                if (!isCustomsOther) continue;
              } else if (imageType && imageType !== "import") {
                continue;
              }

              const alreadyAdded = docs.some(
                (d) =>
                  (doc.ack_id && d.ack_id === doc.ack_id) ||
                  (doc.id && d.id === doc.id)
              );
              if (!alreadyAdded) {
                docs.push({ ...doc, pic_code: type.code });
              }
            }
          }
        }
      }
    }
    setDocuments(docs);
  }, [orderDetails, currentOrder]);

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const normalizeCustomsDoc = (doc = {}) => {
    const rawCode = (doc.pic_code || "").toUpperCase();
    const picCode =
      !rawCode || CUSTOMS_OTHER_ALIASES.includes(rawCode)
        ? "OTHER ATTACHMENT"
        : doc.pic_code;
    const picTitle =
      CUSTOMS_DOC_TYPES.find((t) => t.code === picCode)?.label ||
      doc.pic_title ||
      picCode;
    return {
      ...doc,
      pic_code: picCode,
      pic_title: picTitle,
      image_type: "import",
      picture_type: "import",
    };
  };

  const uploadCustomsFiles = (docsToUpload = []) => {
    const normalizedDocs = (docsToUpload || [])
      .filter((doc) => !!doc?.picture_obj || !!doc?.privacyOnly)
      .map(normalizeCustomsDoc);

    if (normalizedDocs.length === 0) {
      return Promise.resolve(false);
    }

    const newFileDocs = normalizedDocs.filter((doc) => !!doc.picture_obj);

    const itemPictures = normalizedDocs.map((doc) =>
      doc.picture_obj
        ? {
            captured_at: doc.captured_at,
            picture_obj: doc.picture_obj,
            ack_id: doc.ack_id,
            pic_title: doc.pic_title,
            pic_code: doc.pic_code,
            image_type: "import",
            is_private: !!doc.is_private,
            src: doc.src,
          }
        : {
            ack_id: doc.ack_id,
            pic_code: doc.pic_code,
            image_type: "import",
            is_private: !!doc.is_private,
          }
    );

    const payload = {
      refer: "order",
      order_ids: [orderId],
      location_id: locationId,
      pictures: [
        {
          refer_ids: [orderId],
          item_pictures: itemPictures,
        },
      ],
    };

    return uploadCustomsClearanceFile(payload)
      .then((res) => {
        if (res && res.success) {
          setStagedDocsUploadedMap((prev) => {
            const next = { ...prev };
            newFileDocs.forEach((doc) => {
              next[doc.pic_code] = true;
            });
            return next;
          });
          if (newFileDocs.length === 0) {
            message.success("Documents updated successfully");
          } else {
            message.success(
              newFileDocs.length === 1
                ? `${newFileDocs[0].pic_title} uploaded successfully`
                : `${newFileDocs.length} documents uploaded successfully`
            );
          }
          return true;
        }
        message.error(res?.errors || "Failed to upload document");
        const failedAckIds = new Set(normalizedDocs.map((doc) => doc.ack_id));
        setStagedDocs((prev) =>
          prev.filter((doc) => !failedAckIds.has(doc.ack_id))
        );
        return false;
      })
      .catch((err) => {
        console.error("Upload error:", err);
        message.error("An error occurred during upload");
        const failedAckIds = new Set(normalizedDocs.map((doc) => doc.ack_id));
        setStagedDocs((prev) =>
          prev.filter((doc) => !failedAckIds.has(doc.ack_id))
        );
        return false;
      });
  };

  const syncStagedDocsFromBulk = (docs = []) => {
    // Modal close clears files → onDocsChange([]). Keep confirmed create-mode
    // staged docs so they still reach the order-save payload.
    if ((!docs || docs.length === 0) && skipStagedClearOnCloseRef.current) {
      skipStagedClearOnCloseRef.current = false;
      return;
    }

    const normalized = (docs || [])
      .map((doc) => {
        if (doc.picture_obj) return doc;

        // Rows for already-listed docs come back from the modal without their
        // file; re-attach a staged file, or keep the row only if Internal changed
        const existing = documents.find((d) => d.ack_id && d.ack_id === doc.ack_id);
        if (!existing) return null;
        if (existing.picture_obj) {
          return { ...existing, is_private: !!doc.is_private };
        }
        if (!!existing.is_private === !!doc.is_private) return null;
        return { ...existing, is_private: !!doc.is_private, privacyOnly: true };
      })
      .filter(Boolean)
      .map(normalizeCustomsDoc);
    setStagedDocs(normalized);
  };

  const handleChange = async (info, record = {}) => {
    if (info.file.status !== "uploading") {
      const docTypeCode = record.pic_code || "OTHER ATTACHMENT";
      const docTypeLabel =
        CUSTOMS_DOC_TYPES.find((t) => t.code === docTypeCode)?.label ||
        record.pic_title ||
        docTypeCode;
      const file = info.file.originFileObj || info.file;
      const ackId = `${randomUUID()}_n`;

      try {
        const base64Data = await getBase64(file);
        const stagedDoc = normalizeCustomsDoc({
          picture_obj: file,
          captured_at: new Date().toISOString(),
          ack_id: ackId,
          pic_title: docTypeLabel,
          pic_code: docTypeCode,
          isNew: true,
          fileType: file.type || "",
          fileName: file.name || "",
          src: base64Data,
        });

        setStagedDocs((prev) => [...prev, stagedDoc]);
        // Direct bucket drop in view mode uploads immediately (same as Documents)
        if (actionType === "view" && !isModalVisible && orderId) {
          setIsUploadingAll(true);
          uploadCustomsFiles([stagedDoc]).finally(() => {
            setIsUploadingAll(false);
            refreshDetails();
          });
        }
      } catch (error) {
        console.error("Error while converting file:", error);
      }
    }
  };

  const handleMapFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Same constraint as Documents.jsx Map Individual — needs a saved order
    if (!orderId) {
      message.warning("Save the order before mapping individual pages.");
      event.target.value = "";
      return;
    }

    if (file.type === "application/pdf") {
      const fileURL = URL.createObjectURL(file);
      setPreviewFileUrl(fileURL);
      setPreviewFileType("pdf");
      setPreviewVisible(true);
      setShow(true);
    } else {
      message.warning("Please select a PDF file to map individual pages.");
    }

    event.target.value = "";
  };

  // Documents-style bulk upload for an existing/saved order — one API call
  const handleUpload = async () => {
    const toUpload = (stagedDocs || []).filter(
      (doc) => !!doc.picture_obj || !!doc.privacyOnly
    );
    if (toUpload.length === 0) {
      message.warning("Please add files to upload.");
      return;
    }

    setIsUploadingAll(true);
    try {
      const success = await uploadCustomsFiles(toUpload);
      if (success) {
        setStagedDocs([]);
        setIsModalVisible(false);
        refreshDetails();
      }
    } finally {
      setIsUploadingAll(false);
    }
  };

  // Documents-style create flow: keep staged docs for the order-save payload
  const handleCreatePics = () => {
    const toKeep = (stagedDocs || [])
      .filter((doc) => !!doc.picture_obj)
      .map(normalizeCustomsDoc);

    if (toKeep.length === 0) {
      message.warning("Please add files to upload.");
      return;
    }

    setStagedDocs(toKeep);
    setDocuments((prev) => {
      const withoutDupes = prev.filter(
        (doc) => !toKeep.some((staged) => staged.ack_id === doc.ack_id)
      );
      return [...withoutDupes, ...toKeep];
    });
    skipStagedClearOnCloseRef.current = true;
    setIsModalVisible(false);
  };

  const handleDeleteFromBulkModal = (ackIds = []) => {
    if (!ackIds.length) return;

    setStagedDocs((prev) => prev.filter((doc) => !ackIds.includes(doc.ack_id)));
    setDocuments((prev) => prev.filter((doc) => !ackIds.includes(doc.ack_id)));

    if (!orderId) return;

    const apiAckIds = ackIds.filter((id) => {
      const doc = documents.find((d) => d.ack_id === id);
      return !!(doc && (doc.id || doc.pictures_id));
    });
    if (apiAckIds.length === 0) return;

    const data = {
      refer: "order",
      refer_id: orderId,
      ack_id: apiAckIds.join(","),
      account_id:
        currentOrder?.account_id ||
        currentOrder?.account?.account_id ||
        details?.account_id ||
        "",
      organization_id:
        currentOrder?.organization_id ||
        currentOrder?.customer_order_details?.organization_id ||
        details?.organization_id ||
        "",
    };

    deleteOrderPic(data)
      .then((res) => {
        if (res && res.success) {
          message.success("Document(s) deleted successfully");
          refreshDetails();
        } else {
          message.error(res?.errors || "Failed to delete document");
        }
      })
      .catch((err) => {
        console.error("Delete error:", err);
        message.error("An error occurred while deleting document");
      });
  };
  const toggleSelectDocument = (ack_id, pictures_id, check) => {
    if (check) {
      // Preview mode (eye icon) — track the doc for print/download/email
      documents.forEach((doc) => {
        if (doc.ack_id?.includes(ack_id)) {
          setPreviewdoc([{ ack_id, pictures_id }]);
        }
      });
      return;
    }

    // Same as Documents: selecting one file selects every file in that
    // doc-type bucket so print/email/delete apply to the whole group.
    const allDocs = [...documents, ...stagedDocs];
    const clickedDoc = allDocs.find((doc) => doc.ack_id === ack_id);
    const picCode = clickedDoc?.pic_code;
    const isMappedBucket = picCode && predefinedDocsCode.includes(picCode);

    const bucketDocs = isMappedBucket
      ? allDocs.filter(
          (doc) =>
            doc.pic_code === picCode &&
            doc.ack_id &&
            doc.image_type !== "print"
        )
      : allDocs.filter(
          (doc) =>
            doc.ack_id &&
            !predefinedDocsCode.includes(doc.pic_code) &&
            doc.image_type !== "print"
        );

    if (bucketDocs.length === 0) {
      setSelectedDocs((prevSelectedDocs) => {
        const isAlreadySelected = prevSelectedDocs.some(
          (d) => d.ack_id === ack_id
        );
        if (isAlreadySelected) {
          return prevSelectedDocs.filter((d) => d.ack_id !== ack_id);
        }
        return [...prevSelectedDocs, { ack_id, pictures_id }];
      });
      return;
    }

    const bucketAckIds = bucketDocs.map((doc) => doc.ack_id);

    setSelectedDocs((prevSelectedDocs) => {
      const alreadySelectedCount = bucketAckIds.filter((id) =>
        prevSelectedDocs.some((d) => d.ack_id === id)
      ).length;

      if (alreadySelectedCount === bucketAckIds.length) {
        return prevSelectedDocs.filter(
          (d) => !bucketAckIds.includes(d.ack_id)
        );
      }

      const existingAckIds = new Set(prevSelectedDocs.map((d) => d.ack_id));
      const newDocs = bucketDocs
        .filter((doc) => !existingAckIds.has(doc.ack_id))
        .map((doc) => ({
          ack_id: doc.ack_id,
          pictures_id: doc.id || doc.pictures_id,
        }));
      return [...prevSelectedDocs, ...newDocs];
    });
  };

  const selectAll = () => {
    setSelectedDocs(
      [...documents, ...stagedDocs]
        .filter((doc) => doc.ack_id)
        .map((doc) => ({
          ack_id: doc.ack_id,
          pictures_id: doc.id || doc.pictures_id,
        }))
    );
  };

  const deselectAll = () => {
    setSelectedDocs([]);
  };

  const onSendToEmail = async (mailInfo = null) => {
    if (!orderId) {
      message.warning("Save the order before emailing documents.");
      return;
    }
    setIsEmailLoading(true);
    const emailData = mailInfo ? mailInfo : PODemailData;
    const docs = previewVisible ? previewdoc : selectedDocs;
    const docPictureIds = docs.map((doc) => doc.pictures_id).filter(Boolean);
    const data = {
      customer_order_id: orderId,
      picture_ids: docPictureIds,
      location_id: locationId,
      emails: emailData.emails,
      description: emailData.description,
      refer_type: "order",
    };
    try {
      await sendPodImagesToEmails(data);
      setIsEmailLoading(false);
      renderNotification("Email sent successfully", "success");
      setPODemailData({
        emails: [],
        description: "Please find the attached customs documents.",
      });
      setIsPODemailModalVisible(false);
    } catch (error) {
      console.error("Failed to send email:", error);
      setIsEmailLoading(false);
      setIsPODemailModalVisible(false);
    }
  };

  const onPrint = (download = false) => {
    if (!orderId || !locationId) {
      message.warning(
        download
          ? "Save the order before downloading documents."
          : "Save the order before printing documents."
      );
      return;
    }

    const printable = [];
    const docs = previewVisible ? previewdoc : selectedDocs;
    docs?.forEach((doc) => {
      const modifiedAckId = doc.ack_id?.replace("_n", "_p");
      const matchedDoc = documents.find((d) => d.ack_id === modifiedAckId);

      if (matchedDoc?.id) {
        printable.push(matchedDoc.id);
      } else if (doc?.pictures_id) {
        printable.push(doc.pictures_id);
      } else {
        const sourceDoc = documents.find((d) => d.ack_id === doc.ack_id);
        if (sourceDoc?.id) printable.push(sourceDoc.id);
      }
    });

    const pictures_id = printable.join(",");
    if (!pictures_id) {
      message.warning("No attachment found");
      return;
    }

    alertMessage(
      download ? "Downloading in progress" : "Printing in progress",
      "info"
    );

    downloadOrderPictures(orderId, locationId, pictures_id).then((result) => {
      if (result.success) {
        if (!result.data || result.data.length === 0) {
          alertMessage("No attachment found", "error");
          return;
        }

        const base64String = result.data.combine_attachments_pdf;
        if (download) {
          const timestamp = new Date().toISOString().replace(/[:.-]/g, "_");
          const fileName = `Customs_Attachments_${timestamp}.pdf`;
          const byteCharacters = atob(base64String);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "application/pdf" });
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href);
        } else {
          printPDF(base64String);
        }
      } else if (Array.isArray(result.errors) && result.errors.length > 0) {
        renderAlertMessage(result.errors);
      } else {
        alertMessage(
          download ? "Failed to download" : "Failed to print",
          "error"
        );
      }
    });
  };

  const handleDelete = () => {
    const selectedAckIds = selectedDocs.map((doc) => doc.ack_id).filter(Boolean);
    if (selectedAckIds.length === 0) return;

    const inDocuments = selectedAckIds.filter((id) =>
      documents.some((doc) => doc.ack_id === id)
    );
    const inStaged = selectedAckIds.filter((id) =>
      stagedDocs.some((doc) => doc.ack_id === id)
    );

    // Only persisted server pictures should hit the delete API. Local/staged
    // files (no id yet, often still ending in "_n") are removed from UI state only.
    const apiAckIds = inDocuments.filter((id) => {
      const doc = documents.find((d) => d.ack_id === id);
      return !!(doc && (doc.id || doc.pictures_id));
    });

    if (inStaged.length > 0) {
      setStagedDocs((prev) =>
        prev.filter((doc) => !inStaged.includes(doc.ack_id))
      );
    }

    // Always drop selected files from local documents state (create + view)
    if (inDocuments.length > 0) {
      setDocuments((prev) =>
        prev.filter((doc) => !inDocuments.includes(doc.ack_id))
      );
    }

    if (apiAckIds.length > 0 && orderId) {
      const data = {
        refer: "order",
        refer_id: orderId,
        ack_id: apiAckIds.join(","),
        account_id:
          currentOrder?.account_id ||
          currentOrder?.account?.account_id ||
          details?.account_id ||
          "",
        organization_id:
          currentOrder?.organization_id ||
          currentOrder?.customer_order_details?.organization_id ||
          details?.organization_id ||
          "",
      };

      deleteOrderPic(data)
        .then((res) => {
          if (res && res.success) {
            message.success("Document(s) deleted successfully");
            refreshDetails();
          } else {
            message.error(res?.errors || "Failed to delete document");
          }
        })
        .catch((err) => {
          console.error("Delete error:", err);
          message.error("An error occurred while deleting document");
        })
        .finally(() => {
          setSelectedDocs([]);
        });
    } else {
      setSelectedDocs([]);
    }
  };

  const renderSelectionHeader = () => (
    <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
      <div
        style={{
          ...styles.selectionBlock,
          cursor: "pointer",
        }}
        onClick={() =>
          selectedDocs.length === [...documents, ...stagedDocs].length
            ? deselectAll()
            : selectAll()
        }
      >
        <Icon
          type={
            selectedDocs.length === [...documents, ...stagedDocs].length
              ? "check"
              : "minus"
          }
          style={{
            color: "#fff",
            position: "relative",
            top: "-5px",
            left: "3px",
            fontSize: "12px",
          }}
        />
      </div>
      <span style={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600 }}>
        {selectedDocs.length} Selected
      </span>
    </div>
  );

  const canUseDocActions = !!orderId;

  const renderActionButtons = () => (
    <Row
      className="action-buttons-container"
      type="flex"
      justify="space-between"
      align="middle"
      style={{ gap: "8px" }}
    >
      {canUseDocActions && (
        <Col>
          <Tooltip title="Email">
            <div className="action-buttons" data-testid="customs-send-email-button">
              <SendAsEmailIcon
                color="#576EC1"
                style={{ cursor: "pointer" }}
                onClick={() => setIsPODemailModalVisible(true)}
              />
            </div>
          </Tooltip>
        </Col>
      )}
      {canUseDocActions && (
        <Col>
          <Tooltip title="Print">
            <div className="action-buttons" data-testid="customs-print-button">
              <PrintIcon
                width="20px"
                height="20px"
                color="#576EC1"
                style={{ cursor: "pointer" }}
                onClick={() => onPrint()}
              />
            </div>
          </Tooltip>
        </Col>
      )}
      {canUseDocActions && (
        <Col>
          <Tooltip title="Download">
            <div className="action-buttons" data-testid="customs-download-button">
              <DownloadIcon
                width="20px"
                height="20px"
                color="#576EC1"
                style={{ cursor: "pointer" }}
                onClick={() => onPrint(true)}
              />
            </div>
          </Tooltip>
        </Col>
      )}
      <Col>
        <Tooltip title="Delete">
          <div className="trash-action" data-testid="trash-button">
            <TrashIcon
              styling={{ width: "19px", height: "19px" }}
              color="#D23B71"
              style={{ cursor: "pointer" }}
              onClick={handleDelete}
            />
          </div>
        </Tooltip>
      </Col>
    </Row>
  );

  const freightDate = details.freight_arrival_date || null;
  const generalDate = details.general_order_date || null;
  const lastFreeDate = details.last_free_day || null;
  const clearanceDate = details.customs_cleared_date || null;

  const startEdit = () => {
    setFreightArrivalDateVal(freightDate || null);
    setGeneralOrderDateVal(generalDate || null);
    setLastFreeDayVal(lastFreeDate || null);
    setCustomsClearedDateVal(clearanceDate || null);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setFreightArrivalDateVal(freightDate || null);
    setGeneralOrderDateVal(generalDate || null);
    setLastFreeDayVal(lastFreeDate || null);
    setCustomsClearedDateVal(clearanceDate || null);
    setIsEditing(false);
  };

  // Midwest holidays (literal calendar dates; no observed shifts).
  const nthWeekdayOfMonth = (year, monthIndex, weekday, n) => {
    const first = moment({ year, month: monthIndex, day: 1 });
    const offset = (weekday - first.day() + 7) % 7;
    return first.clone().add(offset + (n - 1) * 7, "days");
  };

  const lastWeekdayOfMonth = (year, monthIndex, weekday) => {
    const last = moment({ year, month: monthIndex, day: 1 }).endOf("month").startOf("day");
    const offset = (last.day() - weekday + 7) % 7;
    return last.clone().subtract(offset, "days");
  };

  const mweHolidaysForYear = (year) => {
    const y = Number(year);
    return [
      moment({ year: y, month: 0, day: 1 }), // New Year's Day
      lastWeekdayOfMonth(y, 4, 1), // Memorial Day — last Monday in May
      moment({ year: y, month: 6, day: 4 }), // Fourth of July
      nthWeekdayOfMonth(y, 8, 1, 1), // Labor Day — first Monday in September
      nthWeekdayOfMonth(y, 10, 4, 4), // Thanksgiving — fourth Thursday in November
      moment({ year: y, month: 11, day: 25 }), // Christmas
    ].map((d) => d.format("YYYY-MM-DD"));
  };

  const isWeekend = (date) => {
    const dow = moment(date).isoWeekday(); // 6 = Sat, 7 = Sun
    return dow === 6 || dow === 7;
  };

  const isMweHoliday = (date) => {
    const m = moment(date);
    return mweHolidaysForYear(m.year()).includes(m.format("YYYY-MM-DD"));
  };

  const isNonWorkday = (date) => isWeekend(date) || isMweHoliday(date);

  // Inclusive calendar days: arrival is day 1 of `days` (e.g. 15 → arrival + 14).
  // Weekends and holidays always count.
  const addInclusiveCalendarDays = (date, days) =>
    moment(date).add(Math.max(days - 1, 0), "days");

  const previousWorkday = (date) => {
    let result = moment(date);
    while (isNonWorkday(result)) {
      result = result.subtract(1, "days");
    }
    return result;
  };

  // Inclusive business days from arrival (skip weekends + MWE holidays).
  // If arrival is not a workday, day 1 starts on the next workday.
  const addInclusiveBusinessDays = (date, days) => {
    let result = moment(date);
    let counted = 0;
    while (counted < days) {
      if (!isNonWorkday(result)) {
        counted += 1;
        if (counted === days) break;
      }
      result = result.add(1, "days");
    }
    return result;
  };

  const handleArrivalDateCascade = (date) => {
    if (!date) {
      setGeneralOrderDateVal(null);
      setLastFreeDayVal(null);
      return;
    }

    // GO: arrival inclusive in 15 calendar days, then roll back if weekend/MWE holiday
    const generalOrder = previousWorkday(addInclusiveCalendarDays(date, 15));

    // LFD: from arrival — AIR 3 / OCEAN 4 inclusive business days
    const freightType =
      details.freight_type ||
      currentOrder?.customer_order_details?.freight_type ||
      "AIR";
    const lfdDays = String(freightType).toUpperCase() === "OCEAN" ? 4 : 3;
    const lastFree = addInclusiveBusinessDays(date, lfdDays);

    setGeneralOrderDateVal(generalOrder.format("MM-DD-YYYY"));
    setLastFreeDayVal(lastFree.format("MM-DD-YYYY"));
  };

  // Manual GO edit only — LFD stays independent.
  const handleGeneralOrderDateCascade = (_date, dateString) => {
    setGeneralOrderDateVal(dateString || null);
  };

  const handleSave = async () => {
    const payload = {
      customer_order_params: {
        customer_order_id: currentOrder?.id || orderId,
        warehouse_id:
          currentOrder?.customer_order_details?.warehouse_id || details?.warehouse_id || "",
        organization_id:
          currentOrg?.id ||
          currentOrder?.customer_order_details?.organization_id ||
          currentOrder?.organization_id ||
          details?.organization_id ||
          "",
      },
      options_block_params: {
        freight_arrival_date: freightArrivalDateVal,
        general_order_date: generalOrderDateVal,
        last_free_day: lastFreeDayVal,
        customs_cleared_date: customsClearedDateVal,
        is_customs_cleared: !!customsClearedDateVal,
      },
    };

    try {
      const result = await updateOrderDetails_v2(
        payload.customer_order_params.customer_order_id,
        payload
      );
      if (result?.success) {
        message.success("Customs dates updated successfully");
        setIsCustomsCleared(!!customsClearedDateVal);
        setIsEditing(false);
        refreshDetails();
      } else {
        message.error(result?.errors || "Failed to update customs dates");
      }
    } catch (err) {
      console.error("Save customs dates error:", err);
      message.error("An error occurred while updating customs dates");
    }
  };

  const handleMarkCleared = async () => {

    const today = moment().format("MM-DD-YYYY");
    setCustomsClearedDateVal(today);
    setIsCustomsCleared(true);

    if (viewMode) {
    const payload = {
      customer_order_params: {
        customer_order_id: currentOrder?.id || orderId,
        warehouse_id:
          currentOrder?.customer_order_details?.warehouse_id || details?.warehouse_id || "",
        organization_id:
          currentOrg?.id ||
          currentOrder?.customer_order_details?.organization_id ||
          currentOrder?.organization_id ||
          details?.organization_id ||
          "",
      },
      options_block_params: {
        customs_cleared_date: today,
        is_customs_cleared: true,
      },
    };

    try {
      const result = await updateOrderDetails_v2(
        payload.customer_order_params.customer_order_id,
        payload
      );
      if (result?.success) {
        message.success("Order marked as cleared");
        refreshDetails();
      } else {
        message.error(result?.errors || "Failed to mark order as cleared");
      }
    } catch (err) {
      console.error("Mark cleared error:", err);
      message.error("An error occurred while marking order as cleared");
    }
  }
  };

  const handleRemoveClearance = async () => {
    setCustomsClearedDateVal(null);
    setIsCustomsCleared(false);

    if (viewMode) {
    const payload = {
      customer_order_params: {
        customer_order_id: currentOrder?.id || orderId,
        warehouse_id:
          currentOrder?.customer_order_details?.warehouse_id || details?.warehouse_id || "",
        organization_id:
          currentOrg?.id ||
          currentOrder?.customer_order_details?.organization_id ||
          currentOrder?.organization_id ||
          details?.organization_id ||
          "",
      },
      options_block_params: {
        customs_cleared_date: null,
        is_customs_cleared: false,
      },
    };

    try {
      const result = await updateOrderDetails_v2(
        payload.customer_order_params.customer_order_id,
        payload
      );
      if (result?.success) {
        setIsEditing(false);
        message.success("Clearance removed");
        refreshDetails();
      } else {
        message.error(result?.errors || "Failed to remove clearance");
      }
    } catch (err) {
      console.error("Remove clearance error:", err);
      message.error("An error occurred while removing clearance");
    }
  }
  };

  const formatDateText = (dateVal) =>
    dateVal ? moment(dateVal).format("MM-DD-YYYY") : "—";

  return (
    <Card bordered={false} className="customs-card" bodyStyle={{ padding: 16 }}>
      <Row
        type="flex"
        justify="space-between"
        align="middle"
        className="customs-header"
      >
        <Col>
          <div className="card-title">
            <CustomsOfficerIcon />
            Customs Information
          </div>
        </Col>
        <Col>
          {viewMode ? (
            isEditing ? (
              <div style={{ display: "flex", gap: "8px" }}>
                {isCustomsCleared && (
                  <Tooltip title="Order is already marked as cleared">
                    <Button_v2 className="outline small-btn" onClick={handleRemoveClearance}>
                      Remove clearance
                    </Button_v2>
                  </Tooltip>
                )}
                <Button_v2 className="outline small-btn" onClick={cancelEdit}>
                  Cancel
                </Button_v2>
                <Button_v2 className="raised small-btn" onClick={handleSave}>
                  Save
                </Button_v2>
              </div>
            ) : (
                <div style={{ display: "flex", gap: "8px" }}>
                  {isCustomsCleared ? (
                    <Tooltip title="Order is already marked as cleared">
                      <Tag className="cleared-tag">
                        <CustomsOfficerVerifedIcon />
                        <span>Cleared for Release</span>
                      </Tag>
                    </Tooltip>
                  ) : (
                        <span>
                          <Button_v2
                          className="outline small-btn"
                            onClick={handleMarkCleared}
                          >
                            <CheckMarkIcon width="19px" height="19px" color="#000000" style={{ marginRight: "0px" }} />
                            <span style={{ marginRight: "6px", position: "relative", top: "-1px" }}>Mark Cleared</span>
                          </Button_v2>
                      </span>
                  )}
                  <Button_v2 className="outline small-btn" onClick={startEdit}>
                    Edit
                  </Button_v2>
              </div>
            )
          ) : (
              <div style={{ display: "flex", gap: "8px" }}>
                {isCustomsCleared ? (
                  <Button_v2 className="outline small-btn" onClick={handleRemoveClearance}>
                    Remove clearance
                  </Button_v2>
                ) : (
                      <Button_v2
                      className="outline small-btn"
                        onClick={handleMarkCleared}
                      >
                        <CheckMarkIcon width="19px" height="19px" color="#000000" style={{ marginRight: "0px" }} />
                        <span style={{ marginRight: "6px", position: "relative", top: "-1px" }}>Mark Cleared</span>
                    </Button_v2>
                )}
              </div>
          )}
        </Col>
      </Row>

      <Row gutter={16} className="date-row">
        <Col span={6}>
          <label>Arrival Date</label>
          {viewMode && !isEditing ? (
            <div>{formatDateText(freightDate)}</div>
          ) : (
            <Form.Item className="custom-form-item" name="freight_arrival_date">
              {getFieldDecorator ? getFieldDecorator("freight_arrival_date", {
                initialValue: freightDate ? moment(freightDate) : null,
              })(
                <DatePicker
                  format="MM-DD-YYYY" 
                  style={{ width: "100%" }}
                  placeholder="MM-DD-YYYY"
                  onChange={(date, dateString) => {
                    setFreightArrivalDateVal(dateString || null);
                    handleArrivalDateCascade(date);
                  }}
                />
              ) : (
                <DatePicker
                      format="MM-DD-YYYY" 
                  style={{ width: "100%" }}
                      placeholder="MM-DD-YYYY"
                  value={freightArrivalDateVal ? moment(freightArrivalDateVal) : null}
                  onChange={(date, dateString) => {
                    setFreightArrivalDateVal(dateString || null);
                    handleArrivalDateCascade(date);
                  }}
                />
              )}
            </Form.Item>
          )}
        </Col>
        <Col span={6}>
          <label>General Order Date</label>
          {viewMode && !isEditing ? (
            <div>{formatDateText(generalDate)}</div>
          ) : (
            <Form.Item
              className="custom-form-item"
              name="general_order_date"
              key={generalOrderDateVal || "general_order_date_empty"}
            >
              {getFieldDecorator ? getFieldDecorator("general_order_date", {
                initialValue: generalOrderDateVal ? moment(generalOrderDateVal) : (generalDate ? moment(generalDate) : null),
              })(
                <DatePicker
                  format="MM-DD-YYYY" 
                  style={{ width: "100%" }}
                  placeholder="MM-DD-YYYY"
                  onChange={(date, dateString) =>
                    handleGeneralOrderDateCascade(date, dateString)
                  }
                />
              ) : (
                  <DatePicker
                      format="MM-DD-YYYY" 
                      style={{ width: "100%" }}
                  placeholder="MM-DD-YYYY"
                  value={generalOrderDateVal ? moment(generalOrderDateVal) : null}
                  onChange={(date, dateString) =>
                    handleGeneralOrderDateCascade(date, dateString)
                  }
                />
              )}
            </Form.Item>
          )}
        </Col>

        <Col span={6}>
          <label>Last Free Date</label>
          {viewMode && !isEditing ? (
            <div>{formatDateText(lastFreeDate)}</div>
          ) : (
            <Form.Item
              className="custom-form-item"
              name="last_free_day"
              key={lastFreeDayVal || "last_free_day_empty"}
            >
              {getFieldDecorator ? getFieldDecorator("last_free_day", {
                initialValue: lastFreeDayVal ? moment(lastFreeDayVal) : (lastFreeDate ? moment(lastFreeDate) : null),
              })(
                <DatePicker
                  format="MM-DD-YYYY" 
                  style={{ width: "100%" }}
                  placeholder="MM-DD-YYYY"
                  onChange={(date, dateString) => setLastFreeDayVal(dateString || null)}
                />
              ) : (
                <DatePicker
                      format="MM-DD-YYYY" 
                  style={{ width: "100%" }}
                      placeholder="MM-DD-YYYY"
                  value={lastFreeDayVal ? moment(lastFreeDayVal) : null}
                  onChange={(date, dateString) => setLastFreeDayVal(dateString || null)}
                />
              )}
            </Form.Item>
          )}
        </Col>
        <Col span={6}>
          <label>Clearance</label>
          {viewMode && !isEditing ? (
            isCustomsCleared ? <div>{formatDateText(clearanceDate)}</div> :
              <div className="pending-badge">
                <span className="pending-circle"></span>
                <span className="pending-text">Pending</span>
              </div>
          ) : (
                <div>
                  <Form.Item
                    className="required-star-right custom-form-item"
                    name="customs_cleared_date"
                    key={customsClearedDateVal || "cleared_empty"}
                  >
                    {getFieldDecorator ? getFieldDecorator("customs_cleared_date", {
                      initialValue: customsClearedDateVal ? moment(customsClearedDateVal, "MM-DD-YYYY") : null
                    })(
                      <DatePicker
                        format="MM-DD-YYYY"
                        style={{ width: "100%" }}
                        placeholder="MM-DD-YYYY"
                        onChange={(date, dateString) => {
                          setCustomsClearedDateVal(dateString || null);
                          setIsCustomsCleared(!!dateString);
                        }}
                      />
                    ) : (
                      <DatePicker
                        format="MM-DD-YYYY" 
                        style={{ width: "100%" }}
                        placeholder="MM-DD-YYYY"
                        value={customsClearedDateVal ? moment(customsClearedDateVal, "MM-DD-YYYY") : null}
                        onChange={(date, dateString) => {
                          setCustomsClearedDateVal(dateString || null);
                          setIsCustomsCleared(!!dateString);
                        }}
                      />
                    )}
                  </Form.Item>
              </div>
          )}
        </Col>

      </Row>

      <div className="documents-wrapper">
        <Row
          type="flex"
          justify="space-between"
          align="middle"
          className="document-header"
        >
          <Col>
            {selectedDocs.length > 0 ? (
              renderSelectionHeader()
            ) : (
                <div className="document-heading">Customs Documents</div>
            )}
          </Col>
          <Col>
            {selectedDocs.length > 0 ? (
              renderActionButtons()
            ) : (
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <Button_v2
                  type="secondary"
                  className="outline small-btn"
                  text="Map Individual Pages"
                  data-testid="customs-map-individual-button"
                  onClick={() => document.getElementById(mapFileInputId)?.click()}
                />
                <input
                  type="file"
                  id={mapFileInputId}
                  accept="application/pdf"
                  style={{ display: "none" }}
                  onChange={handleMapFileSelect}
                />
                <Button_v2
                  type="secondary"
                  className="outline small-btn"
                  text="Upload"
                  data-testid="customs-upload-document-button"
                  onClick={() => setIsModalVisible(true)}
                />
              </div>
            )}
          </Col>
        </Row>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "24px",
            flexWrap: "wrap",
          }}
        >
          {CUSTOMS_DOC_TYPES.map((type) => (
            <UploadButton_v2
              key={type.code}
              label={type.label}
              docCode={type.code}
              documents={documents}
              stagedDocs={stagedDocs}
              selectedDocs={selectedDocs}
              handleChange={handleChange}
              toggleSelectDocument={toggleSelectDocument}
              predefinedDocsCode={predefinedDocsCode}
              onPrint={onPrint}
              requestedAs="mapped_upload"
              setIsPODemailModalVisible={setIsPODemailModalVisible}
              currentOrder={previewOrder}
              setPreviewVisible={setPreviewVisible}
              previewVisible={previewVisible}
              setPreviewFileUrl={setPreviewFileUrl}
              previewFileUrl={previewFileUrl}
              setPreviewFileType={setPreviewFileType}
              previewFileType={previewFileType}
              refreshDetails={refreshDetails}
              show={show}
              setShow={setShow}
              locObj={locObj}
              isShipper={false}
              previewDocs={previewdoc}
              handleDelete={handleDeleteFromBulkModal}
              handleUpload={handleUpload}
              setStagedDocs={syncStagedDocsFromBulk}
              isUploading={isUploadingAll}
              picSettings={customsPicSettings}
              actionType={actionType}
              handleCreatePics={handleCreatePics}
              allowInternalToggle
              hideDetailsColumn
            />
          ))}
        </div>
      </div>

      <BulkAttachmentUpload
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onUpload={actionType === "view" ? handleUpload : handleCreatePics}
        onDocsChange={syncStagedDocsFromBulk}
        tags={customsPicSettings}
        onDelete={handleDeleteFromBulkModal}
        isUploading={isUploadingAll}
        picSettings={customsPicSettings}
        useTagsOnly
        allowInternalToggle
        hideDetailsColumn
        fallbackPicCode="OTHER ATTACHMENT"
      />

      {show && previewVisible && previewFileType === "pdf" && (
        <FilePreviewer
          file={null}
          previewVisible={previewVisible}
          setPreviewVisible={setPreviewVisible}
          previewFileUrl={previewFileUrl}
          setPreviewFileUrl={setPreviewFileUrl}
          previewFileType={previewFileType}
          setPreviewFileType={setPreviewFileType}
          setIsHovered={() => {}}
          predefinedDocsCode={predefinedDocsCode}
          requestedAs="mapped_upload"
          toggleSelectDocument={toggleSelectDocument}
          onPrint={onPrint}
          setIsPODemailModalVisible={setIsPODemailModalVisible}
          currentOrder={previewOrder}
          show={show}
          refreshDetails={refreshDetails}
          locObj={locObj}
          isImported={true}
        />
      )}

      <PODEmailDrawer
        description="Please find the attached customs documents."
        currentOrder={previewOrder}
        accountId={
          previewOrder?.account?.account_id ||
          details?.account_id ||
          ""
        }
        isPODemailDrawerOpen={isPODemailModalVisible}
        setIsPODemailDrawerOpen={setIsPODemailModalVisible}
        onSendToEmail={onSendToEmail}
        isEmailLoading={isEmailLoading}
        isClosable={true}
      />
    </Card>
  );
};

const styles = {
  selectionBlock: {
    width: "18px",
    height: "18px",
    borderRadius: "2px",
    backgroundColor: "#576EC1",
    transition: "all 0.3s ease-in-out",
  },
};

export default CustomsInformation;
